import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Dependency-free i18n for the SPA (EN/ES/DE).
 *
 * Why not i18next: the build runs offline (no registry egress), so we keep a tiny
 * in-house layer instead of adding a dependency. It provides:
 *   - a persisted, browser-detected current language (localStorage `jalupro.lang`),
 *   - `useT(dict)` — a scoped translator over a per-screen dictionary,
 *   - `{{var}}` interpolation and an English fallback (then the key itself).
 *
 * Each screen owns a self-contained `Dict` (en + es + de maps) so screens never share
 * a translation file — there is no central catalog to merge. Cross-cutting strings
 * (nav, status pills, shared feedback) live in `./common`.
 */

export type Lang = 'en' | 'es' | 'de';
export const LANGS: readonly Lang[] = ['en', 'es', 'de'];
export const LANG_LABEL: Record<Lang, string> = {
  en: 'English',
  es: 'Español',
  de: 'Deutsch',
};
/** Flag emoji per language (English → UK flag, Spanish → Spain flag, German → DE flag). */
export const LANG_FLAG: Record<Lang, string> = { en: '🇬🇧', es: '🇪🇸', de: '🇩🇪' };

/** Default language when the user has made no choice yet (Jalupro is a Spanish company). */
export const DEFAULT_LANG: Lang = 'es';

const STORAGE_KEY = 'jalupro.lang';

export function isLang(v: unknown): v is Lang {
  return v === 'en' || v === 'es' || v === 'de';
}

/** Persisted choice (pre-login, localStorage) → default 'es'. */
function detectLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLang(saved)) return saved;
  } catch {
    /* localStorage may be unavailable (private mode); fall through */
  }
  return DEFAULT_LANG;
}

/** A per-screen (or shared) translation table: English + Spanish + German.
 *  All three are required so a new screen can't silently ship without a German
 *  block (the compiler flags any dict missing `de`). */
export type Dict = {
  readonly en: Readonly<Record<string, string>>;
  readonly es: Readonly<Record<string, string>>;
  readonly de: Readonly<Record<string, string>>;
};

export type TFunc = (key: string, vars?: Record<string, string | number>) => string;

interface I18nContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectLang);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore persistence failures */
    }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang }), [lang, setLang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useLang(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useLang must be used within <I18nProvider>');
  return ctx;
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, name: string) =>
    name in vars ? String(vars[name]) : `{{${name}}}`,
  );
}

/**
 * Returns a translator scoped to `dict`: `t(key, vars?)` resolves against the current
 * language, falling back to English, then to the key itself. `dict` should be a stable
 * module-level constant.
 */
export function useT(dict: Dict): TFunc {
  const { lang } = useLang();
  return useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const table = dict[lang] ?? dict.en;
      const template = table[key] ?? dict.en[key] ?? key;
      return interpolate(template, vars);
    },
    [dict, lang],
  );
}
